import { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronDown, Download, FolderOpen, Link2, Save, Sparkles, Trash2, Upload } from 'lucide-react';
import clsx from 'clsx';
import { STARTER_PRESETS } from '../../presets/starters';
import { deserialisePreset, documentToShareUrl, serialisePreset } from '../../core/export/preset';
import { deletePreset, listPresets, savePreset, type StoredPreset } from '../../core/export/presetStore';
import { downloadText } from '../../core/export/download';
import { useStudio } from '../../store/studioStore';

export function PresetMenu(): JSX.Element {
  const doc = useStudio((s) => s.doc);
  const replaceDocument = useStudio((s) => s.replaceDocument);

  const [open, setOpen] = useState(false);
  const [presets, setPresets] = useState<StoredPreset[]>([]);
  const [tab, setTab] = useState<'starter' | 'own'>('starter');
  const [name, setName] = useState('');
  const [note, setNote] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const refresh = useCallback(() => {
    void listPresets().then(setPresets);
  }, []);

  useEffect(() => {
    if (open) refresh();
  }, [open, refresh]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent): void => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const flash = (text: string): void => {
    setNote(text);
    setTimeout(() => setNote(null), 2600);
  };

  const load = (preset: StoredPreset): void => {
    try {
      const { doc: loaded, droppedLayers } = deserialisePreset(preset.file);
      replaceDocument(loaded, `Preset: ${preset.name}`);
      setOpen(false);
      if (droppedLayers.length) {
        flash(`Geladen, ${droppedLayers.length} unbekannte Technik-Layer übersprungen.`);
      }
    } catch (err) {
      flash((err as Error).message);
    }
  };

  const importFile = async (file: File): Promise<void> => {
    try {
      const { doc: loaded, droppedLayers } = deserialisePreset(JSON.parse(await file.text()));
      replaceDocument(loaded, `Preset: ${file.name}`);
      setOpen(false);
      flash(droppedLayers.length ? `Geladen, ${droppedLayers.length} Layer übersprungen.` : 'Preset geladen.');
    } catch (err) {
      flash((err as Error).message);
    }
  };

  const share = async (): Promise<void> => {
    const url = documentToShareUrl(doc, window.location.href.split('#')[0]);
    try {
      await navigator.clipboard.writeText(url);
      flash(`Link kopiert — ${(url.length / 1024).toFixed(1)} kB`);
    } catch {
      // Clipboard access can be denied; putting the link in the address bar still lets
      // the user copy it manually.
      window.location.hash = new URL(url).hash;
      flash('Link steht in der Adresszeile.');
    }
  };

  return (
    <div ref={ref} className="relative">
      <button type="button" onClick={() => setOpen(!open)} className="ghost-button gap-1 px-2 py-1 text-[11px]">
        Preset
        <ChevronDown size={11} />
      </button>

      {note && (
        <div className="absolute left-0 top-9 z-[60] whitespace-nowrap rounded bg-ink-800 px-2 py-1 text-[10px] text-accent-500">
          {note}
        </div>
      )}

      {open && (
        <div className="absolute left-0 top-8 z-50 w-[320px] rounded-lg border border-ink-700 bg-ink-850 shadow-2xl shadow-black/60">
          <div className="flex gap-0.5 border-b border-ink-750 p-1.5">
            {(['starter', 'own'] as const).map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setTab(t)}
                className={clsx(
                  'flex-1 rounded px-2 py-1 text-[11px] transition-colors',
                  tab === t ? 'bg-ink-750 text-ink-100' : 'text-ink-400 hover:text-ink-200',
                )}
              >
                {t === 'starter' ? `Startpunkte (${STARTER_PRESETS.length})` : 'Eigene'}
              </button>
            ))}
          </div>

          {tab === 'starter' && (
            <div className="max-h-[380px] overflow-y-auto py-1">
              {STARTER_PRESETS.map((starter) => (
                <button
                  key={starter.id}
                  type="button"
                  onClick={() => {
                    replaceDocument(starter.build(), `Startpunkt: ${starter.name}`);
                    setOpen(false);
                  }}
                  className="block w-full px-3 py-1.5 text-left transition-colors hover:bg-ink-800"
                >
                  <div className="flex items-center gap-1.5">
                    <Sparkles size={10} className="shrink-0 text-accent-500" />
                    <span className="text-[11px] text-ink-100">{starter.name}</span>
                  </div>
                  <div className="mt-0.5 pl-[16px] text-[10px] leading-relaxed text-ink-500">{starter.note}</div>
                </button>
              ))}
              <p className="border-t border-ink-750 px-3 py-2 text-[10px] leading-relaxed text-ink-600">
                Fertige Ausgangspunkte — Schrift, Format, Palette und Technik aufeinander abgestimmt. Laden ist ein
                Undo-Schritt wie jeder andere.
              </p>
            </div>
          )}

          {tab === 'own' && (
            <>
          <div className="flex items-center gap-1.5 border-b border-ink-750 p-2">
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Name des Presets"
              className="field-input text-[11px]"
              onKeyDown={(e) => {
                if (e.key !== 'Enter') return;
                void savePreset(serialisePreset(doc, name.trim() || 'Ohne Titel')).then(() => {
                  setName('');
                  refresh();
                  flash('Gespeichert.');
                });
              }}
            />
            <button
              type="button"
              title="In diesem Browser speichern"
              onClick={() =>
                void savePreset(serialisePreset(doc, name.trim() || 'Ohne Titel')).then(() => {
                  setName('');
                  refresh();
                  flash('Gespeichert.');
                })
              }
              className="ghost-button h-7 w-7 shrink-0 p-0"
            >
              <Save size={13} />
            </button>
          </div>

          <div className="max-h-[280px] overflow-y-auto py-1">
            {presets.length === 0 && (
              <p className="px-3 py-4 text-center text-[10px] leading-relaxed text-ink-500">
                Noch keine Presets. Der komplette Zustand — Text, Layer, Parameter, Palette, Seed — wird
                gespeichert.
              </p>
            )}
            {presets.map((preset) => (
              <div key={preset.id} className="group/preset flex items-center gap-1 px-2 py-1 hover:bg-ink-800">
                <button type="button" onClick={() => load(preset)} className="min-w-0 flex-1 text-left">
                  <div className="truncate text-[11px] text-ink-100">{preset.name}</div>
                  <div className="truncate font-mono text-[9px] text-ink-600">
                    {new Date(preset.createdAt).toLocaleString('de-CH')} ·{' '}
                    {preset.file.doc.layers.length} Layer
                  </div>
                </button>
                <button
                  type="button"
                  title="Als Datei herunterladen"
                  onClick={() =>
                    downloadText(
                      JSON.stringify(preset.file, null, 2),
                      `${preset.name.replace(/[\\/:*?"<>|]+/g, '-')}.typo.json`,
                      'application/json',
                    )
                  }
                  className="ghost-button h-6 w-6 shrink-0 p-0 opacity-0 group-hover/preset:opacity-100"
                >
                  <Download size={11} />
                </button>
                <button
                  type="button"
                  title="Löschen"
                  onClick={() => void deletePreset(preset.id).then(refresh)}
                  className="ghost-button h-6 w-6 shrink-0 p-0 opacity-0 hover:text-red-400 group-hover/preset:opacity-100"
                >
                  <Trash2 size={11} />
                </button>
              </div>
            ))}
          </div>

          <div className="flex border-t border-ink-750">
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="flex flex-1 items-center justify-center gap-1.5 py-2 text-[10px] text-ink-300 transition-colors hover:bg-ink-800 hover:text-ink-100"
            >
              <Upload size={11} />
              Datei laden
            </button>
            <button
              type="button"
              onClick={() =>
                downloadText(
                  JSON.stringify(serialisePreset(doc, name.trim() || 'Export'), null, 2),
                  `${(name.trim() || 'komposition').replace(/[\\/:*?"<>|]+/g, '-')}.typo.json`,
                  'application/json',
                )
              }
              className="flex flex-1 items-center justify-center gap-1.5 border-x border-ink-750 py-2 text-[10px] text-ink-300 transition-colors hover:bg-ink-800 hover:text-ink-100"
            >
              <FolderOpen size={11} />
              Als Datei
            </button>
            <button
              type="button"
              onClick={() => void share()}
              title="Kompletten Zustand als Link in die Zwischenablage"
              className="flex flex-1 items-center justify-center gap-1.5 py-2 text-[10px] text-ink-300 transition-colors hover:bg-ink-800 hover:text-ink-100"
            >
              <Link2 size={11} />
              Link
            </button>
          </div>

          <input
            ref={fileRef}
            type="file"
            accept=".json,application/json"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void importFile(file);
              e.target.value = '';
            }}
          />
            </>
          )}
        </div>
      )}
    </div>
  );
}
